import { useState, useEffect } from 'react'
import { AgCharts } from 'ag-charts-react'
import type { AgCartesianChartOptions } from 'ag-charts-community'
import {
  dashboardService,
  DashboardData,
  DashboardPeriod,
  DashboardStat,
  DashboardTransaction,
  MonthlyBillingData
} from '../services/dashboardService'
import Alert from '../components/Alert'

const Dashboard = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<DashboardPeriod>('30d')
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null)
  const [monthlyData, setMonthlyData] = useState<MonthlyBillingData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fallback sample data for AG Charts - will be replaced by API data
  const fallbackBillingData = [
    { month: 'Jan', sent: 2400, opened: 1560, replied: 468, converted: 124 },
    { month: 'Feb', sent: 2800, opened: 1820, replied: 546, converted: 145 },
    { month: 'Mar', sent: 3200, opened: 2080, replied: 624, converted: 166 },
    { month: 'Apr', sent: 2900, opened: 1885, replied: 565, converted: 150 },
    { month: 'May', sent: 3500, opened: 2275, replied: 682, converted: 181 },
    { month: 'Jun', sent: 3800, opened: 2470, replied: 741, converted: 197 }
  ]

  // Use real data if available, otherwise fallback
  const billingData = monthlyData.length > 0 ? monthlyData : fallbackBillingData

  // Chart options for billing metrics
  const billingChartOptions: AgCartesianChartOptions = {
    background: {
      fill: 'transparent',
    },
    data: billingData,
    series: [
      {
        type: 'line',
        xKey: 'month',
        yKey: 'sent',
        yName: 'Emails Sent',
        stroke: '#3b82f6',
        marker: {
          enabled: true,
          fill: '#3b82f6',
        },
      },
      {
        type: 'line',
        xKey: 'month',
        yKey: 'opened',
        yName: 'Emails Opened',
        stroke: '#10b981',
        marker: {
          enabled: true,
          fill: '#10b981',
        },
      },
      {
        type: 'line',
        xKey: 'month',
        yKey: 'replied',
        yName: 'Replies',
        stroke: '#f59e0b',
        marker: {
          enabled: true,
          fill: '#f59e0b',
        },
      },
    ],
    axes: [
      {
        type: 'category',
        position: 'bottom',
        title: {
          text: 'Month',
          fontSize: 14,
          fontWeight: 'bold',
        },
        label: {
          color: '#ffffff',
        },
        line: {
          color: '#ffffff',
        },
        tick: {
          color: '#ffffff',
        },
      },
      {
        type: 'number',
        position: 'left',
        title: {
          text: 'Count',
          fontSize: 14,
          fontWeight: 'bold',
        },
        label: {
          color: '#ffffff',
          formatter: (params: any) => params.value.toLocaleString(),
        },
        line: {
          color: '#ffffff',
        },
        tick: {
          color: '#ffffff',
        },
        gridLine: {
          style: [
            {
              stroke: '#374151',
              lineDash: [5, 5],
            },
          ],
        },
      },
    ],
    legend: {
      enabled: true,
      position: 'bottom',
      item: {
        label: {
          color: '#ffffff',
        },
      },
    }
  } as any

  const fetchDashboardStats = async (period: DashboardPeriod) => {
    try {
      setLoading(true)
      setError(null)

      // Fetch dashboard stats and monthly data in parallel
      const [dashboardStatsData, monthlyBillingData] = await Promise.all([
        dashboardService.getDashboardStats(period),
        dashboardService.getMonthlyBillingData(period).catch(() => []) // Use the selected period
      ])

      setDashboardData(dashboardStatsData)
      setMonthlyData(monthlyBillingData)
    } catch (err) {
      console.error('Error fetching dashboard stats:', err)
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDashboardStats(selectedPeriod)
  }, [selectedPeriod])

  const handlePeriodChange = (period: string) => {
    setSelectedPeriod(period as DashboardPeriod)
  }

  if (loading) {
    return (
      <div className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Alert */}
        {error && (
          <Alert
            type="error"
            title="Error loading dashboard data"
            message={error}
            onAction={() => fetchDashboardStats(selectedPeriod)}
            actionLabel="Try again"
            onDismiss={() => setError(null)}
            testId="error-container"
          />
        )}

        {/* Show content only if data is loaded */}
        {dashboardData && (
          <>
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Dashboard</h1>
              <p className="text-slate-600 dark:text-slate-300 mt-2">
                Overview of your billing management and subscription metrics
              </p>
              {dashboardData.dateRange && (
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  Data from {new Date(dashboardData.dateRange.startDate).toLocaleDateString()} to {new Date(dashboardData.dateRange.endDate).toLocaleDateString()}
                </p>
              )}
            </div>

            {/* Period Selector */}
            <div className="mb-8">
              <div className="flex space-x-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-lg w-fit" data-testid="period-selector">
                {['24h', '7d', '30d', '90d', '1y'].map((period) => (
                  <button
                    key={period}
                    onClick={() => handlePeriodChange(period)}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${selectedPeriod === period
                      ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                      : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
                      }`}
                    data-testid={`period-${period}`}
                  >
                    {period === '24h' && 'Last 24 hours'}
                    {period === '7d' && 'Last 7 days'}
                    {period === '30d' && 'Last 30 days'}
                    {period === '90d' && 'Last 90 days'}
                    {period === '1y' && 'Last year'}
                  </button>
                ))}
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-testid="stats-grid">
              {dashboardData.stats.map((stat) => (
                <div
                  key={stat.name}
                  className="bg-white dark:bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700"
                  data-testid={`stat-${stat.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
                        {stat.name}
                      </p>
                      <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1" data-testid="stat-value">
                        {stat.value}
                      </p>
                    </div>
                    <div
                      className={`flex items-center text-sm font-medium ${stat.changeType === 'positive'
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                        }`}
                      data-testid="stat-change"
                    >
                      <svg
                        className={`w-4 h-4 mr-1 ${stat.changeType === 'positive' ? 'rotate-0' : 'rotate-180'
                          }`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L6.707 7.707a1 1 0 01-1.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {stat.change}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Charts Section */}
            <div className="mb-8">
              {/* Billing Performance Chart */}
              <div className="bg-white dark:bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
                  Billing Performance
                </h3>
                <div>
                  <AgCharts options={billingChartOptions} />
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700" data-testid="recent-transactions">
              <div className="p-6 border-b border-slate-200 dark:border-slate-700">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Recent Activity
                </h3>
              </div>
              <div className="p-6">
                {dashboardData.recentTransactions.length > 0 ? (
                  <div className="space-y-4" data-testid="transactions-list">
                    {dashboardData.recentTransactions.map((transaction) => (
                      <div
                        key={transaction.id}
                        className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-700 rounded-lg"
                        data-testid={`transaction-${transaction.id}`}
                      >
                        <div className="flex items-center space-x-3">
                          <div
                            className={`w-2 h-2 rounded-full ${transaction.type === 'response'
                              ? 'bg-green-500'
                              : 'bg-blue-500'
                              }`}
                            data-testid="transaction-type-indicator"
                          />
                          <div>
                            <p className="font-medium text-slate-900 dark:text-white" data-testid="transaction-description">
                              {transaction.description}
                            </p>
                            <p className="text-sm text-slate-600 dark:text-slate-300" data-testid="transaction-date">
                              {new Date(transaction.date).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <span
                          className={`font-semibold ${transaction.type === 'response'
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-blue-600 dark:text-blue-400'
                            }`}
                          data-testid="transaction-amount"
                        >
                          {transaction.amount}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8" data-testid="empty-transactions">
                    <p className="text-slate-500 dark:text-slate-400">No recent activity found</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default Dashboard 